/**
 * Portal-wide helpers. All tenants share one hostname: admin pages live under /admin,
 * and a vendor's tenant comes from their login rather than the URL.
 */

/**
 * Get API base URL
 */
export function getApiBaseUrl(): string {
  // In development, use the proxy
  if (import.meta.env.DEV) {
    return '/api';
  }

  // In production, use the backend server URL from the build environment
  return import.meta.env.VITE_API_BASE_URL;
}

/**
 * Apply tenant branding (colors, logo)
 */
// Tenants created before the Flank Speed branding default to Tailwind's generic blue; treat that
// as "no custom colour" so they get the brand accent from index.css instead.
const LEGACY_DEFAULT_COLOR = '#3b82f6';

export function applyTenantBranding(primaryColor?: string) {
  if (primaryColor && primaryColor.toLowerCase() !== LEGACY_DEFAULT_COLOR) {
    document.documentElement.style.setProperty('--color-primary', primaryColor);

    // Generate darker and lighter variants
    const hex = primaryColor.replace('#', '');
    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);

    // Darker variant (reduce by 15%)
    const darkerR = Math.max(0, Math.floor(r * 0.85));
    const darkerG = Math.max(0, Math.floor(g * 0.85));
    const darkerB = Math.max(0, Math.floor(b * 0.85));
    const darker = `#${darkerR.toString(16).padStart(2, '0')}${darkerG.toString(16).padStart(2, '0')}${darkerB.toString(16).padStart(2, '0')}`;
    document.documentElement.style.setProperty('--color-primary-dark', darker);

    // Lighter variant (increase by 15%)
    const lighterR = Math.min(255, Math.floor(r * 1.15));
    const lighterG = Math.min(255, Math.floor(g * 1.15));
    const lighterB = Math.min(255, Math.floor(b * 1.15));
    const lighter = `#${lighterR.toString(16).padStart(2, '0')}${lighterG.toString(16).padStart(2, '0')}${lighterB.toString(16).padStart(2, '0')}`;
    document.documentElement.style.setProperty('--color-primary-light', lighter);
  } else {
    // No custom colour: back to the brand accent, not the previous tenant's colour
    document.documentElement.style.removeProperty('--color-primary');
    document.documentElement.style.removeProperty('--color-primary-dark');
    document.documentElement.style.removeProperty('--color-primary-light');
  }
}

/**
 * True when the current page belongs to the admin portal
 */
export function isAdminPath(pathname: string = window.location.pathname): boolean {
  return pathname === '/admin' || pathname.startsWith('/admin/');
}
